/**
 * @author SwathiDivya Bhavaraju
 * @email swathidivya.bhavaraju@ul.com
 * @create date 2020-04-02 17:55:01
 * @modify date 2020-04-02 17:55:01
 * @desc [description]
 */
<template>
  <div id="app">
    <notifications position="bottom right" />
    <custom-notification />
    <mfa-popup :data="mfaData" />
    <router-view />
  </div>
</template>

<script>
import $ from 'jquery';
import CustomNotification from './components/Common/CustomNotification.vue';
import MfaPopup from './components/Common/MFApopup.vue';

const JAVA_API_BASE_URL = (process.env.VUE_APP_JAVA_API_URL || '').replace(/\/+$/, '');
const HEALTH_CHECK_URL = JAVA_API_BASE_URL
  ? `${JAVA_API_BASE_URL}/actuator/health`
  : '/services/actuator/health';
const HEALTH_CHECK_RETRY_COUNT = 2;
const HEALTH_CHECK_RETRY_DELAY = 1500;
const HEALTH_CHECK_INTERVAL = 45000;
const MAINTENANCE_TRIGGER_KEY = '__maintenanceTriggered';
const MAINTENANCE_STATIC_RELOAD_KEY = '__maintenanceStaticReloadDone';

const wait = (delay) => new Promise((resolve) => {
  setTimeout(resolve, delay);
});

export default {
  name: 'App',
  components: {
    CustomNotification,
    MfaPopup
  },
  data() {
    return {
      mfaData: {},
      healthCheckIntervalRef: null
    };
  },
  watch: {
    '$route.path': {
      handler(path) {
        if (path === '/login') {
          this.maybeReloadForStaticMaintenancePage();
        }
      }
    }
  },
  mounted() {
    if (!window[MAINTENANCE_TRIGGER_KEY]) {
      window[MAINTENANCE_TRIGGER_KEY] = false;
    }
    this.startHealthCheckPolling();
    if (!window.navigator.onLine) {
      this.$router.push('/networkconnection').catch(() => {});
    } 
    this.emitter.on('multipleApiRequests', (message) => {
       this.$notify({
        clear: true,
        group: 'custom-notification'
      });
      this.$notify({
        type: 'error',
        title: 'Error',
        text: message,
        group: 'custom-notification'
      });
    });
    this.emitter.on('openMFAPopup', (data) => {
      this.mfaData = data;
      $('#mfaModal').modal({
        backdrop: 'static',
        keyboard: false
      });
    });
  },
  beforeUnmount() {
    if (this.healthCheckIntervalRef) {
      clearInterval(this.healthCheckIntervalRef);
      this.healthCheckIntervalRef = null;
    }
    this.emitter.off('multipleApiRequests');
    this.emitter.off('openMFAPopup');
  },
  methods: {
    forceLogout() {
      if (window[MAINTENANCE_TRIGGER_KEY]) return;
      window[MAINTENANCE_TRIGGER_KEY] = true;
      if (window.emitter) {
        window.emitter.emit('logout');
      } else {
        document.cookie = 'Authorization=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
        document.cookie = 'redirect_url=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
        localStorage.clear();
        sessionStorage.clear();
        this.$router.push('/login');
      }
    },
    async maybeReloadForStaticMaintenancePage() {
      if (sessionStorage.getItem(MAINTENANCE_STATIC_RELOAD_KEY)) return;

      let unhealthy = false;
      try {
        const response = await fetch(HEALTH_CHECK_URL, {
          method: 'GET',
          credentials: 'omit'
        });
        if (!response.ok) {
          unhealthy = true;
        } else {
          const health = await response.json();
          if (health?.status === 'DOWN') unhealthy = true;
        }
      } catch (error) {
        console.log('Health check failed (login)', error);
        unhealthy = true;
      }

      if (!unhealthy) {
        sessionStorage.removeItem(MAINTENANCE_STATIC_RELOAD_KEY);
        return;
      }

      sessionStorage.setItem(MAINTENANCE_STATIC_RELOAD_KEY, '1');
      window.location.reload();
    },
    async checkHealthWithRetry(attempt = 0) {
      if (this.$route?.path === '/login') {
        await this.maybeReloadForStaticMaintenancePage();
        return;
      }

      if (window[MAINTENANCE_TRIGGER_KEY]) return;

      try {
        const response = await fetch(HEALTH_CHECK_URL, {
          method: 'GET',
          credentials: 'omit'
        });
        if (!response.ok) throw new Error('Health check failed');
        const health = await response.json();
        if (health?.status && health.status !== 'DOWN') return;
      } catch (error) {
        // Retry will handle temporary deployment/network blips.
        console.log('Health check failed', error);
      }

      if (attempt >= HEALTH_CHECK_RETRY_COUNT) {
        this.forceLogout();
        return;
      }

      await wait(HEALTH_CHECK_RETRY_DELAY);
      await this.checkHealthWithRetry(attempt + 1);
    },
    startHealthCheckPolling() {
      this.checkHealthWithRetry();
      this.healthCheckIntervalRef = setInterval(() => {
        this.checkHealthWithRetry();
      }, HEALTH_CHECK_INTERVAL);
    }
  }
};
</script>
